import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { QuickstartTheme } from '../types/theme';
import {
  AnimationConfig,
  ANIMATION_PRESETS,
  ANIMATIONS_ENABLED,
  getInitialState,
  getAnimateState,
  getExitState,
  getTransition,
} from '../config/animations';

/**
 * Convert hex color to rgba with opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Hook to detect if device is mobile
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Tailwind's md breakpoint
    };

    // Check on mount
    checkMobile();

    // Listen for window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

/**
 * Hook to detect if device is in landscape mode
 */
function useIsLandscape(): boolean {
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const checkLandscape = () => {
      setIsLandscape(window.innerHeight < window.innerWidth && window.innerHeight < 600);
    };

    // Check on mount
    checkLandscape();

    // Listen for window resize
    window.addEventListener('resize', checkLandscape);
    return () => window.removeEventListener('resize', checkLandscape);
  }, []);

  return isLandscape;
}

/**
 * Shared card component for all quickstart screens
 * Themed based on game configuration
 */
interface QuickstartCardProps {
  theme: QuickstartTheme;
  children: React.ReactNode;
  className?: string;
  animation?: AnimationConfig;
  showHeader?: boolean;
  badgeOverride?: string; // Override the release date badge with custom text (e.g., "SNEAK PEEK")
  hideBadge?: boolean; // Hide the release date badge completely
  hideFooter?: boolean; // Hide the "Powered by Playcast" footer logo
  hideStudioLogo?: boolean; // Hide the studio logo (e.g., DeadDrop)
}

export const QuickstartCard = ({
  theme,
  children,
  className = '',
  animation = ANIMATION_PRESETS.cardDefault,
  showHeader = true,
  badgeOverride,
  hideBadge = false,
  hideFooter = false,
  hideStudioLogo = false,
}: QuickstartCardProps) => {
  // Use 'none' animation if globally disabled
  const effectiveAnimation = ANIMATIONS_ENABLED ? animation : ANIMATION_PRESETS.none;

  // Detect if mobile and landscape
  const isMobile = useIsMobile();
  const isLandscape = useIsLandscape();

  // Get modal background with opacity (transparent on mobile)
  const modalOpacity = isMobile ? 0 : (theme.modalBackgroundOpacity ?? 0.95);
  const modalBackground = hexToRgba(theme.modalBackgroundColor, modalOpacity);

  // Adjust padding and sizing for landscape mode
  const contentPadding = isLandscape ? 'px-4 py-2' : 'px-8 py-4';
  const maxWidth = isLandscape ? 'max-w-2xl' : 'max-w-md';

  return (
    <motion.div
      className={`relative mx-auto flex w-full ${maxWidth} flex-col items-center rounded-lg ${isMobile ? '' : 'shadow-lg'} ${className}`}
      style={{
        backgroundColor: modalBackground,
      }}
      initial={getInitialState(effectiveAnimation)}
      animate={getAnimateState(effectiveAnimation)}
      exit={getExitState(effectiveAnimation)}
      transition={getTransition(effectiveAnimation)}
    >
      {showHeader && (
        <>
          {/* Studio Logo - flush to top */}
          {!hideStudioLogo && theme.studioLogo && (
            <img src={theme.studioLogo} alt={theme.studioName} className={`${isLandscape ? 'mt-2 h-10' : 'mt-4 h-16'}`} />
          )}

          {/* Game Logo or Game Name */}
          {theme.gameLogo ? (
            <img src={theme.gameLogo} alt={theme.gameName} className={`${isLandscape ? 'mt-2 h-12' : 'mt-12 h-20'}`} />
          ) : (
            <h1 className={`font-bold ${isLandscape ? 'mt-2 text-2xl' : 'mt-12 text-3xl'}`} style={{ color: theme.primaryColor }}>
              {theme.gameName}
            </h1>
          )}

          {/* Badge - Shows Badge Override or Release Date if provided */}
          {!hideBadge && (badgeOverride || theme.releaseDate) && (
            <div
              className={`rounded px-3 py-1 text-center font-["Inter"] font-semibold ${isLandscape ? 'mb-2 mt-2 text-lg' : 'mb-6 mt-8 text-2xl'}`}
              style={{
                color: badgeOverride
                  ? theme.badgeOverrideTextColor || theme.releaseDateTextColor || theme.modalBackgroundColor
                  : theme.releaseDateTextColor || theme.modalBackgroundColor,
              }}
            >
              {badgeOverride ? (
                <div>{badgeOverride}</div>
              ) : (
                <>
                  <div>COMING:</div>
                  <div>{theme.releaseDate}</div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Content - with padding */}
      <div className={`w-full ${contentPadding}`}>{children}</div>

      {/* Powered by Playcast - flush to bottom */}
      {!hideFooter && <img src='/quickstart/pbp.png' alt={theme.gameName} className={`h-6 ${isLandscape ? 'mb-2 mt-4' : 'mb-4 mt-12'}`} />}
    </motion.div>
  );
};

